package com.rosario.juridico.platform

import com.rosario.juridico.marketplace.model.Abogado
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.model.Propuesta
import com.rosario.juridico.marketplace.model.Expediente
import com.rosario.juridico.marketplace.model.Movimiento
import com.rosario.juridico.marketplace.model.Tarea
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.*
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.ExperimentalCoroutinesApi

import com.rosario.juridico.firebase.FirebaseWeb

actual object FirebaseProxy {
    private var isRedirectHandled = false

    suspend fun handleRedirectResult() {
        if (isRedirectHandled) return
        isRedirectHandled = true
        try {
            FirebaseWeb.initializeIfNeeded()
            val authModule = js("require('firebase/auth')")
            val jsAuth = authModule.getAuth()
            val jsPromise = authModule.getRedirectResult(jsAuth) as kotlin.js.Promise<dynamic>
            val result = jsPromise.await()
            if (result != null && result != undefined) {
                val user = result.user
                val uid = user?.uid as? String
                val email = user?.email as? String
                println("[FirebaseProxy] getRedirectResult successful. User UID: $uid, email: $email")
                if (uid != null) {
                    initializeUserIfNeeded(uid)
                }
            } else {
                println("[FirebaseProxy] getRedirectResult: No redirect result pending.")
            }
        } catch (e: Exception) {
            println("[FirebaseProxy] Error in getRedirectResult: ${e.message}")
        }
    }

    actual fun getCurrentUserId(): String? = FirebaseWeb.auth.currentUser?.uid
    actual fun getUserEmail(): String? = FirebaseWeb.auth.currentUser?.email
    actual fun isAnonymous(): Boolean = FirebaseWeb.auth.currentUser?.isAnonymous ?: true
    actual fun isLoggedIn(): Boolean {
        val user = FirebaseWeb.auth.currentUser
        return user != null && !user.isAnonymous
    }
    actual fun isEmailVerified(): Boolean = FirebaseWeb.auth.currentUser?.isEmailVerified ?: false
    
    actual suspend fun reloadUser(): Boolean {
        return try {
            FirebaseWeb.auth.currentUser?.reload()
            true
        } catch (e: Exception) {
            false
        }
    }

    actual suspend fun sendEmailVerification(): AuthResult {
        return try {
            FirebaseWeb.auth.currentUser?.sendEmailVerification()
            AuthResult.Success(FirebaseWeb.auth.currentUser?.uid ?: "")
        } catch (e: Exception) {
            AuthResult.Error(e.message ?: "Unknown error", AuthErrorCode.UNKNOWN)
        }
    }

    private val anonymousAuthMutex = Mutex()

    actual suspend fun signInAnonymously(): String? {
        val existingUid = FirebaseWeb.auth.currentUser?.uid
        if (existingUid != null) return existingUid

        return anonymousAuthMutex.withLock {
            val lockedUid = FirebaseWeb.auth.currentUser?.uid
            if (lockedUid != null) return@withLock lockedUid
            try {
                FirebaseWeb.auth.signInAnonymously().user?.uid
            } catch (e: Exception) {
                null
            }
        }
    }

    actual suspend fun getIdToken(): String? {
        return try {
            FirebaseWeb.auth.currentUser?.getIdToken(false)
        } catch (e: Exception) {
            null
        }
    }

    actual fun getAccountCreationTimestamp(): Long {
        return try {
            val metadata = FirebaseWeb.auth.currentUser?.asDynamic()?.metadata
            val creationTime = metadata?.creationTime?.toString()
            if (creationTime != null) {
                kotlinx.datetime.Instant.parse(creationTime).toEpochMilliseconds()
            } else 0L
        } catch (e: Exception) {
            0L
        }
    }
    
    actual suspend fun signIn(email: String, password: String): AuthResult {
        return try {
            val result = FirebaseWeb.auth.signInWithEmailAndPassword(email, password)
            AuthResult.Success(result.user?.uid ?: "")
        } catch (e: Exception) {
            AuthResult.Error(e.message ?: "Login failed", AuthErrorCode.UNKNOWN)
        }
    }

    actual suspend fun signUp(email: String, password: String): AuthResult {
        return try {
            val result = FirebaseWeb.auth.createUserWithEmailAndPassword(email, password)
            AuthResult.Success(result.user?.uid ?: "")
        } catch (e: Exception) {
            AuthResult.Error(e.message ?: "Sign up failed", AuthErrorCode.UNKNOWN)
        }
    }

    actual suspend fun signInWithGoogle(idToken: String): AuthResult {
        return try {
            println("[FirebaseProxy] Initiating Google Sign-In with Popup (Web)...")
            FirebaseWeb.initializeIfNeeded()

            val authModule = js("require('firebase/auth')")
            val jsAuth = authModule.getAuth()
            val provider = js("new authModule.GoogleAuthProvider()")
            provider.setCustomParameters(kotlin.js.json("prompt" to "select_account"))

            val jsPromise = authModule.signInWithPopup(jsAuth, provider) as kotlin.js.Promise<dynamic>
            val result = jsPromise.await()

            val uid = result.user?.uid as? String ?: ""
            if (uid.isNotEmpty()) {
                initializeUserIfNeeded(uid)
                AuthResult.Success(uid)
            } else {
                AuthResult.Error("No se pudo obtener el UID del usuario", AuthErrorCode.UNKNOWN)
            }
        } catch (e: Throwable) {
            println("[FirebaseProxy] Error Kotlin Login: ${e.message}")
            AuthResult.Error(e.message ?: "Google login failed", AuthErrorCode.UNKNOWN)
        }
    }

    actual suspend fun sendPasswordResetEmail(email: String): AuthResult {
        return try {
            FirebaseWeb.auth.sendPasswordResetEmail(email)
            AuthResult.Success("")
        } catch (e: Exception) {
            AuthResult.Error(e.message ?: "Reset failed", AuthErrorCode.UNKNOWN)
        }
    }

    actual suspend fun logout() {
        try {
            FirebaseWeb.auth.signOut()
        } catch (e: Exception) {}
    }
    
    actual suspend fun getConsultas(tipoConsulta: String?): List<Consulta> = emptyList()
    actual suspend fun getConsultasPendientes(tipoConsulta: String?): List<Consulta> = emptyList()
    actual suspend fun aprobarConsulta(consultaId: String) {}
    actual suspend fun rechazarConsulta(consultaId: String, motivo: String) {}
    actual suspend fun eliminarConsulta(consultaId: String) {}

    actual suspend fun getPropuestas(consultaId: String): List<Propuesta> = emptyList()
    
    actual suspend fun getAbogado(uid: String): Abogado? {
        return try {
            val doc = FirebaseWeb.firestore.collection("abogados").document(uid).get()
            if (doc.exists) {
                val nom = try { doc.get<String?>("nombre") } catch (_: Exception) { null }
                val ape = try { doc.get<String?>("apellido") } catch (_: Exception) { null }
                val email = try { doc.get<String?>("email") } catch (_: Exception) { null }
                val tel = try { doc.get<String?>("telefono") } catch (_: Exception) { null }
                val mat = try { doc.get<String?>("matricula") } catch (_: Exception) { null }
                val sub = try { doc.get<Boolean?>("isSubscribed") } catch (_: Exception) { false }
                val niv = try { doc.get<String?>("nivelVerificacion") } catch (_: Exception) { null }

                Abogado(
                    id = uid,
                    nombre = nom.orEmpty().ifBlank { "Abogado" },
                    apellido = ape.orEmpty(),
                    email = email.orEmpty(),
                    telefono = tel.orEmpty(),
                    matricula = mat.orEmpty(),
                    isSubscribed = sub ?: false,
                    nivelVerificacion = niv.orEmpty()
                )
            } else null
        } catch (e: Exception) {
            println("Error al obtener abogado en JS: ${e.message}")
            null
        }
    }

    actual suspend fun getAbogadosDestacados(): List<Abogado> = emptyList()
    
    actual suspend fun saveAbogado(abogado: Abogado) {
        try {
            val data = mapOf(
                "id" to abogado.id,
                "nombre" to abogado.nombre,
                "apellido" to abogado.apellido,
                "email" to abogado.email,
                "telefono" to abogado.telefono.orEmpty(),
                "matricula" to abogado.matricula.orEmpty(),
                "isSubscribed" to abogado.isSubscribed,
                "nivelVerificacion" to abogado.nivelVerificacion
            )
            FirebaseWeb.firestore.collection("abogados").document(abogado.id).set(data)
        } catch (e: Exception) {
            println("Error al guardar abogado en JS: ${e.message}")
        }
    }
    actual suspend fun updateSubscription(uid: String, subscribed: Boolean) {}
    actual suspend fun addConsulta(consulta: Consulta) {}
    actual suspend fun addPropuesta(propuesta: Propuesta) {}
    actual suspend fun aceptarPropuesta(consultaId: String) {}
    actual suspend fun startTrial(uid: String) {
        try {
            val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
            val thirtyDays = 30L * 24 * 60 * 60 * 1000
            FirebaseWeb.firestore.collection("users").document(uid)
                .update("trialExpiresAt" to (now + thirtyDays).toDouble())
        } catch (e: Exception) {}
    }

    actual suspend fun consumeFreeUse(uid: String) {
        try {
            val userDoc = FirebaseWeb.firestore.collection("users").document(uid)
            val snapshot = userDoc.get()
            val current = try { 
                snapshot.get<Int>("initial_uses_remaining") ?: 0
            } catch (e: Exception) { 0 }
            
            if (current > 0) {
                userDoc.update("initial_uses_remaining" to (current - 1))
            }
        } catch (e: Exception) {
            println("Error consuming free use: ${e.message}")
        }
    }

    actual suspend fun initializeUserIfNeeded(uid: String) {
        try {
            val userDoc = FirebaseWeb.firestore.collection("users").document(uid)
            val snapshot = userDoc.get()
            if (!snapshot.exists) {
                // Bug 3.3 Fix: Usar mapa con Double para evitar error de Long en JS
                val data = mapOf(
                    "uid" to uid,
                    "premium" to false,
                    "initial_uses_remaining" to 5,
                    "monthly_uses_remaining" to 3,
                    "last_monthly_use_timestamp" to null,
                    "trialExpiresAt" to 0.0,
                    "vencimiento" to 0.0
                )
                userDoc.set(data)
            }
        } catch (e: Exception) {
            println("Error initializing user: ${e.message}")
        }
    }

    actual suspend fun checkAndConsumeUsage(uid: String): AccessResult {
        return try {
            val userRef = FirebaseWeb.firestore.collection("users").document(uid)
            FirebaseWeb.firestore.runTransaction {
                val snapshot = get(userRef)
                
                // Auditoría Fase 2: Garantizar acceso ilimitado a Premium en Firestore (Web)
                // Validamos flag premium y fecha de vencimiento activa o indefinida.
                val isPremium = try { snapshot.get<Boolean>("premium") ?: false } catch(e: Exception) { false }
                val vencimiento = try { snapshot.get<Double?>("vencimiento")?.toLong() ?: 0L } catch (e: Exception) { 0L }
                val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
                
                val isPremiumActive = isPremium && (vencimiento <= 0L || vencimiento > now)

                if (isPremiumActive) {
                    return@runTransaction AccessResult.ALLOWED_INITIAL
                }

                // Bug 1 Fix (Web): Lectura robusta de campos en JS usando snapshot.get
                val initialRemaining = try { snapshot.get<Int>("initial_uses_remaining") } catch(e: Exception) { 5 }
                val monthlyRemaining = try { snapshot.get<Int>("monthly_uses_remaining") } catch(e: Exception) { 3 }
                val lastUse = try { snapshot.get<Double?>("last_monthly_use_timestamp")?.toLong() } catch(e: Exception) { null }

                println("AppDebug Web: Transaction read - remaining: $initialRemaining, monthly: $monthlyRemaining, lastUse: $lastUse")

                val quota = UserQuota(
                    initial_uses_remaining = initialRemaining,
                    monthly_uses_remaining = monthlyRemaining,
                    last_monthly_use_timestamp = lastUse
                )
                
                val result = checkAccess(quota)
                val nowDouble = now.toDouble() // Bug 3.3 Fix: Cast Long to Double for JS Interop
                val thirtyDaysMillis = 30L * 24 * 60 * 60 * 1000

                when (result) {
                    AccessResult.ALLOWED_INITIAL -> {
                        update(userRef, mapOf(
                            "initial_uses_remaining" to (initialRemaining - 1).toDouble()
                        ))
                        AccessResult.ALLOWED_INITIAL
                    }
                    AccessResult.ALLOWED_MONTHLY -> {
                        val lastUseLong = lastUse
                        if (lastUseLong == null || (now - lastUseLong) >= thirtyDaysMillis) {
                            // Reinicio de ciclo
                            update(userRef, mapOf(
                                "monthly_uses_remaining" to 2.0,
                                "last_monthly_use_timestamp" to nowDouble
                            ))
                        } else {
                            // Consumo dentro del ciclo
                            update(userRef, mapOf(
                                "monthly_uses_remaining" to (monthlyRemaining - 1).toDouble()
                            ))
                        }
                        AccessResult.ALLOWED_MONTHLY
                    }
                    AccessResult.DENIED -> {
                        AccessResult.DENIED
                    }
                }
            }
        } catch (e: Exception) {
            println("Error in checkAndConsumeUsage Web: ${e.message}")
            AccessResult.DENIED
        }
    }

    // Agenda de Expedientes
    actual suspend fun getExpedientes(userId: String): List<Expediente> = emptyList()
    actual suspend fun getTareas(expedienteId: String): List<Tarea> = emptyList()
    actual suspend fun addExpediente(userId: String, expediente: Expediente): String = ""
    actual suspend fun updateExpediente(userId: String, expediente: Expediente) {}
    actual suspend fun addTarea(expedienteId: String, tarea: Tarea): String = ""
    actual suspend fun updateExpedienteArchivado(userId: String, expedienteId: String, archivado: Boolean) {}
    actual suspend fun eliminarExpediente(userId: String, expedienteId: String) {}

    // Movimientos procesales
    actual suspend fun getMovimientos(userId: String, expedienteId: String): List<Movimiento> = emptyList()
    actual suspend fun addMovimiento(userId: String, expedienteId: String, movimiento: Movimiento): String = ""
    actual suspend fun updateMovimiento(userId: String, expedienteId: String, movimiento: Movimiento) {}
    actual suspend fun eliminarMovimiento(userId: String, expedienteId: String, movimientoId: String) {}

    @OptIn(ExperimentalCoroutinesApi::class)
    actual fun observeUserStatus(): Flow<UserStatus> {
        return flow {
            handleRedirectResult()
            emitAll(FirebaseWeb.auth.authStateChanged)
        }.flatMapLatest { user ->
            if (user == null) {
                // Paso 1: Usuario nulo en Web -> Iniciar Auth Anónimo y BLOQUEAR
                println("AppDebug Web: No hay sesión. Iniciando Auth Anónimo...")
                CoroutineScope(Dispatchers.Main).launch {
                    try {
                        signInAnonymously()
                    } catch (e: Exception) {
                        println("AppDebug Web: Error en signInAnonymously: ${e.message}")
                    }
                }
                flowOf(UserStatus(isInitialized = false, isLoading = true))
            } else {
                val uid = user.uid
                FirebaseWeb.firestore.collection("users").document(uid)
                    .snapshots.map { snapshot ->
                        try {
                            if (!snapshot.exists) {
                                println("AppDebug Web: Documento no existe para UID: $uid. Inicializando...")
                                CoroutineScope(Dispatchers.Default).launch {
                                    initializeUserIfNeeded(uid)
                                }
                                UserStatus(isInitialized = false, isLoading = true)
                            } else {
                                val isPremium = snapshot.get<Boolean>("premium") ?: false
                                val isAdmin = try { snapshot.get<Boolean>("isAdmin") ?: false } catch (_: Exception) { false }
                                val rol = try { snapshot.get<String>("rol") } catch (e: Exception) { "indefinido" }
                                val expAt = try { snapshot.get<Number>("vencimiento")?.toLong() ?: 0L } catch (e: Exception) { 0L }
                                val trialAt = try { snapshot.get<Number>("trialExpiresAt")?.toLong() ?: 0L } catch (e: Exception) { 0L }
                                val remaining = try { snapshot.get<Int>("initial_uses_remaining") } catch (e: Exception) { 5 }
                                val monthlyRemaining = try { snapshot.get<Int>("monthly_uses_remaining") } catch (e: Exception) { 3 }
                                val lastUse = try { snapshot.get<Double?>("last_monthly_use_timestamp")?.toLong() } catch (e: Exception) { null }

                                println("AppDebug Web: Documento leído. UID: $uid, Uses: $remaining, Monthly: $monthlyRemaining")

                                UserStatus(
                                    isInitialized = true, // SÓLO AQUÍ ES TRUE
                                    isPremium = isPremium,
                                    isAdmin = isAdmin || rol.equals("admin", ignoreCase = true),
                                    rol = rol,
                                    premiumExpiresAt = expAt,
                                    trialExpiresAt = trialAt,
                                    initial_uses_remaining = remaining,
                                    monthly_uses_remaining = monthlyRemaining,
                                    last_monthly_use_timestamp = lastUse,
                                    isLoading = false,
                                    consistency = com.rosario.juridico.premium.PremiumManager.entitlement.consistency
                                )
                            }
                        } catch (e: Exception) {
                            println("AppDebug Web: Error parsing UserStatus para UID $uid: ${e.message}")
                            UserStatus(isInitialized = false, isLoading = true)
                        }
                    }
            }
        }
    }

    actual fun observeAuthState(): Flow<String?> {
        return flow {
            handleRedirectResult()
            emitAll(FirebaseWeb.auth.authStateChanged.map { it?.uid })
        }
    }

    actual fun handlePaymentReturn(onSuccess: () -> Unit) {
        try {
            val url = kotlinx.browser.window.location.href
            if (url.contains("payment_status=success")) {
                kotlinx.browser.window.history.replaceState(null, "", "/")
                
                // Forzar sincronización inmediata con polling para asegurar el estado Premium
                MainScope().launch {
                    var attempts = 0
                    val maxAttempts = 4
                    
                    // Activamos estado visual de carga en el Paywall si el usuario vuelve allí
                    com.rosario.juridico.premium.PremiumPurchaseManager.setProcessingState()
                    
                    // Polling para esperar al webhook (2s, 4s, 6s, 8s)
                    while (attempts < maxAttempts) {
                        attempts++
                        delay(2000L * attempts)
                        
                        com.rosario.juridico.premium.PremiumManager.syncWithServer()
                        
                        if (com.rosario.juridico.premium.PremiumManager.isPremium) {
                            onSuccess()
                            return@launch
                        }
                    }
                    
                    // Si después del polling aún no es premium, navegamos igual para no frustrar al usuario,
                    // la UI del Paywall ocultará el botón apenas Firestore lo notifique vía snapshots.
                    onSuccess()
                }
            }
        } catch (e: Exception) {
            // Ignorar si no hay ventana
        }
    }

    actual suspend fun callFunction(name: String, data: Map<String, Any?>): String? {
        return try {
            FirebaseWeb.functions.httpsCallable(name).invoke(data)
            "success"
        } catch (e: Exception) {
            println("Error calling function $name: ${e.message}")
            null
        }
    }

    actual suspend fun canjearPromo(codigo: String): PromoResult {
        return try {
            FirebaseWeb.functions.httpsCallable("canjearPromo").invoke(mapOf("codigo" to codigo))
            PromoResult.Success
        } catch (e: Exception) {
            val message = e.message ?: "Error desconocido"
            val cleanMessage = when {
                message.contains("invalid-argument") -> "Código no válido"
                message.contains("already-exists") -> "Este código ya fue utilizado"
                else -> "Error al canjear el código"
            }
            PromoResult.Error(cleanMessage)
        }
    }
}
